import { IMoment, moment } from '../moment';
import {
  DrivingPeriodViewModel,
  IDrivingPeriod,
  IServerChangedAttribute,
  TRIP_TRIGGER_SOURCE,
} from './driving-period-view-model';
import { mapRestrictionReason, RESTRICTION_REASON } from './assign-restriction-reason';

import { IServerNavigation } from '@misc/detail-view-navigation/server-navigation.interface';
import { IServerDispatcher, IServerLogVehicle, IServerDriver, ILogServerData } from '@app/dashboard/logs/log';
import { IServerSuggestedDriver, ITripsLabelData } from '@services/trips-list';
import { ComplianceFeatureFlagService } from '@services/feature-flags/compliance';

import { VEHICLE_REGULATION_MODES } from './../vehicle-regulation-modes';
import { fullName } from '../utils';
import { DriverIdOriginTags } from '@lib/data/driver-identification';
import { mapSdsEventType } from '@lib/compliance-utils';

export interface IServerDrivingPeriod {
  id: number;
  start_time: string;
  start_time_zone: string;
  end_time: string;
  end_time_zone: string;
  start_kilometers: number;
  end_kilometers: number;
  start_description: string;
  trigger?: TRIP_TRIGGER_SOURCE;
  assignable: boolean;
  unassignable: boolean;
  status: string;
  receiving_log: ILogServerData;
  type: string;
  log_ids: number[];
  note: string;
  changed_attributes: IServerChangedAttribute;
  restriction_reason: string;
  is_claimed: boolean;
  _navigation: IServerNavigation;
  vehicle: IServerLogVehicle;
  driver_facing_images: Array<{
    driver_facing_img_url: string;
    created_at: string;
    img_creation_timestamp: string;
  }>;
  suggested_driver?: IServerSuggestedDriver;
  dispatcher: IServerDispatcher;
  driver: IServerDriver;
  start_address: string;
  end_address: string;
  end_description: string;
  distance: number;
  notes: string;
  log_suggestion_id: number;
  start_event_offline_id: string;
  sds_type?: string;
  editable: boolean;
  regulation_mode: VEHICLE_REGULATION_MODES;
  hideDriverSuggestion: boolean;
  locations: Array<{
    location: IServerDpLocation;
  }>;
  prev_period?: IServerNextPeriod;
  next_period?: IServerNextPeriod;
  driving_period_nav?: IServerDrivingPeriodNav;
  driver_id_origin_tag?: DriverIdOriginTags;
}

export interface IServerDrivingPeriodNav {
  next_driving_period: IServerNextPrevDrivingPeriodNav;
  prev_driving_period: IServerNextPrevDrivingPeriodNav;
}

interface IServerNextPrevDrivingPeriodNav {
  id: number;
  start_time: string;
}

export interface IServerNextPeriod {
  driver: IServerDriver;
  end_time?: string;
  start_time?: string;
}

export interface IServerDpLocation {
  lat: number;
  lon: number;
  bearing: number;
}

export interface IDpLocation {
  lat: number;
  lng: number;
  bearing: number;
}

export class DrivingPeriod {
  public data: IServerDrivingPeriod;

  constructor(data: IServerDrivingPeriod, private complianceFeatureFlagService?: ComplianceFeatureFlagService) {
    this.data = data;
  }

  get id(): number {
    return this.data.id;
  }

  get startTime(): IMoment {
    return moment(this.data.start_time);
  }

  get endTime(): IMoment {
    return moment(this.data.end_time || undefined);
  }

  get duration(): number {
    return this.endTime.diff(this.startTime, 'seconds');
  }

  get startKilometers(): number {
    return this.data.start_kilometers;
  }

  get endKilometers(): number {
    return this.data.end_kilometers;
  }

  get startLocation(): string {
    return this.data.start_description;
  }

  get endLocation(): string {
    return this.data.end_description;
  }

  get isComplete(): boolean {
    const data = this.data;
    return Boolean(data.start_time && data.end_time);
  }

  get isAssignable(): boolean {
    return this.data.assignable;
  }

  get showReassignButton(): boolean {
    return this.data.type === 'driving';
  }

  get overlapsWithCurrentDay(): boolean {
    const now = moment();
    return this.startTime.isSame(now, 'day') || this.endTime.isSame(now, 'day');
  }

  get logIds(): number[] {
    return this.data.log_ids;
  }

  get note(): string {
    return this.data.note;
  }

  get isClaimed(): boolean {
    return this.data.is_claimed;
  }

  get restrictionReason(): RESTRICTION_REASON {
    return mapRestrictionReason(this.data.restriction_reason);
  }

  get navigation(): IServerNavigation {
    return this.data._navigation;
  }

  set navigation(navigation: IServerNavigation) {
    this.data._navigation = navigation;
  }

  get sdsType(): string {
    return this.data.sds_type ? mapSdsEventType(this.data.sds_type) : '';
  }

  set sdsType(sdsType: string) {
    this.data.sds_type = sdsType;
  }

  get drivingPeriodNav(): IServerDrivingPeriodNav {
    return this.data.driving_period_nav;
  }

  set drivingPeriodNav(drivingPeriodNav: IServerDrivingPeriodNav) {
    this.data.driving_period_nav = drivingPeriodNav;
  }

  get viewModel(): IDrivingPeriod {
    const viewModel = new DrivingPeriodViewModel(this, this.complianceFeatureFlagService);
    return viewModel.get();
  }

  get unassignable(): boolean {
    return this.data.unassignable;
  }

  get labelAndTooltipData(): ITripsLabelData {
    return {
      driver: this.data.driver,
      suggestedDriver: this.data.suggested_driver,
      notes: this.data.notes,
      changedAttributes: { ...this.data.changed_attributes, driver: this.data.receiving_log?.driver ?? null },
      dispatcher: this.data.dispatcher ? { name: fullName(this.data.dispatcher), id: this.data.dispatcher?.id } : null,
      vehicleNumber: this.data.vehicle?.number,
      isClaimed: this.data.is_claimed,
      identifiedByIdCard: this.data.driver && this.data.driver_id_origin_tag === DriverIdOriginTags.rfid,
      driverIdOriginTag: this.data.driver_id_origin_tag,
    };
  }
}
